import React, { useEffect, useState } from "react";
import { Form, Button, Row, Col, Image, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../services/api";
import "../css/UserProfile.css";

const UserProfile = () => {
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({});
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imgUploading, setImgUploading] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [loyalty, setLoyalty] = useState({ enabled: false });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await api.get("/users/me");
        setProfile(res.data);
        setFormData(res.data);
      } catch (err) {
        console.error("Error fetching user profile:", err);
      }
    };
    fetchProfile();
  }, []);

  // Loyalty wallet (points balance + recent history). Shown only when the
  // program is enabled by the admin.
  useEffect(() => {
    api.get("/loyalty/public").then((r) => setLoyalty(r.data)).catch(() => {});
    api.get("/loyalty/wallet").then((r) => setWallet(r.data)).catch(() => {});
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImgUploading(true);

    try {
      const data = new FormData();
      data.append("profileImage", file);

      const res = await api.put("/users/me", data, {
        headers: { 
          "Content-Type": "multipart/form-data",
          // Bearer token should be automatically handled by your api service
        },
      });

      setProfile(res.data);
      setFormData(res.data); // Update formData as well
      setPreview(URL.createObjectURL(file));
      alert(t("account.photoUpdated"));
    } catch (err) {
      console.error("Image upload error:", err);
      alert(t("account.photoUploadFailed"));
    } finally {
      setImgUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Option 1: Send as JSON (recommended for text data only)
      const res = await api.put("/users/me", {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber,
      }, {
        headers: { 
          "Content-Type": "application/json"
        },
      });

      // Option 2: If your API specifically requires FormData for all updates
      // const data = new FormData();
      // data.append("firstName", formData.firstName);
      // data.append("lastName", formData.lastName);
      // data.append("phoneNumber", formData.phoneNumber);
      
      // const res = await api.put("/users/me", data, {
      //   headers: { 
      //     "Content-Type": "multipart/form-data"
      //   },
      // });

      setProfile(res.data);
      setFormData(res.data); // Keep formData in sync
      alert(t("account.profileUpdated"));
    } catch (err) {
      console.error("Profile update error:", err);
      alert(t("account.profileUpdateFailed"));
    } finally {
      setLoading(false);
    }
  };

  if (!profile) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" variant="warning" />
      </div>
    );
  }

  return (
    <div className="container my-5">
      <Row>
        {/* Left - Sidebar */}
        <Col md={3} className="text-center mb-4">
          <div className="d-flex flex-column align-items-start gap-2">
            <Button variant="link" className="text-dark ps-0 fw-bold">
              {t("account.profile")}
            </Button>
            <Button variant="link" className="text-dark ps-0" onClick={() => navigate("/my-orders")}>
              {t("nav.myOrders")}
            </Button>
            <Button variant="link" className="text-dark ps-0" onClick={() => navigate("/my-addresses")}>
              {t("nav.myAddresses")}
            </Button>
          </div>
        </Col>

        {/* Vertical line */}
        <Col md={1} className="d-none d-md-flex justify-content-center">
          <div style={{ width: "1px", backgroundColor: "#ddd", height: "100%" }}></div>
        </Col>

        {/* Right - Main Content */}
        <Col md={8}>
          {loyalty.enabled && (
            <div className="bg-white p-4 rounded shadow-sm mb-4">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h5 className="fw-bold mb-0">🎁 {t("account.pointsWallet")}</h5>
                <span className="fw-bold" style={{ color: "#1a5c38", fontSize: "1.4rem" }}>
                  {(wallet?.balance ?? 0).toLocaleString()} pts
                </span>
              </div>
              <div className="text-muted small mb-3">
                {t("account.earnRate", { points: loyalty.earn_points, won: Number(loyalty.earn_per_won || 0).toLocaleString() })}
                {loyalty.redeem_won_per_point ? ` · ${t("account.redeemRate", { won: Number(loyalty.redeem_won_per_point).toLocaleString() })}` : ""}
              </div>
              {wallet?.transactions?.length > 0 ? (
                <ul className="list-unstyled mb-0">
                  {wallet.transactions.slice(0, 6).map((t) => (
                    <li key={t._id} className="d-flex justify-content-between border-bottom py-2 small">
                      <span className="text-capitalize">
                        {t.type}{t.note ? ` · ${t.note}` : ""}
                      </span>
                      <span className={t.points >= 0 ? "text-success fw-bold" : "text-danger fw-bold"}>
                        {t.points >= 0 ? "+" : ""}{t.points.toLocaleString()}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-muted small">
                  {t("account.noPointsYet")}
                </div>
              )}
            </div>
          )}
          <Form onSubmit={handleSubmit} className="bg-white p-4 rounded shadow-sm">
            <div className="d-flex align-items-center gap-3 mb-4">
              <Image
                src={
                  preview ||
                  profile.profileImageUrl ||
                  "https://cdn-icons-png.flaticon.com/512/847/847969.png"
                }
                roundedCircle
                style={{ width: 80, height: 80, objectFit: "cover", border: "2px solid #ffc107" }}
              />
              <div className="d-flex flex-column">
                <Form.Control 
                  type="file" 
                  id="profileImageInput"
                  onChange={handleFileChange} 
                  size="sm" 
                  accept="image/*"
                />
                <Button
                  size="sm"
                  variant="warning"
                  className="mt-2"
                  onClick={() => document.getElementById("profileImageInput").click()}
                  disabled={imgUploading}
                >
                  {imgUploading ? t("success.uploading") : t("account.updatePhoto")}
                </Button>
              </div>
            </div>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t("auth.firstName")}</Form.Label>
                  <Form.Control
                    type="text"
                    name="firstName"
                    value={formData.firstName || ""}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t("auth.lastName")}</Form.Label>
                  <Form.Control
                    type="text"
                    name="lastName"
                    value={formData.lastName || ""}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>{t("auth.phoneNumber")}</Form.Label>
              <Form.Control
                type="text"
                name="phoneNumber"
                value={formData.phoneNumber || ""}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("auth.email")}</Form.Label>
              <Form.Control type="email" value={formData.email || ""} readOnly />
            </Form.Group>

            <Button variant="warning" type="submit" className="fw-bold" disabled={loading}>
              {loading ? t("auth.updating") : t("account.updateProfile")}
            </Button>
          </Form>
        </Col>
      </Row>
    </div>
  );
};

export default UserProfile;